import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import styles from "@/components/preventiveCare/preventiveCareList.module.css";
import {
  Box,
  Button,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import dayjs from "dayjs";
import { IDropDownOption } from "@/types/DropDown";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { replaceDate } from "@/helper/dateHandling";
import DateRangeFilterModal from "@/components/dateTime/dateRangeFilterModal";
import DateRangeFilterChip from "@/components/dateTime/dateRangeFilterChip";
import { api, apiRequest } from "@/helper/api";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import GenericNotification from "@/components/notification/genericNotification";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import AddIcon from "@mui/icons-material/Add";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import DropDown from "@/components/forms/inputs/dropDown";
import ExportMenu, { ExportMenuItem } from "@/components/appointmentRequest/exportMenu";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";
import { subjectExaminationDeadlineEntityAdapter } from "@/components/preventiveCare/subjectExaminationDeadlineEntityAdapter";

const PreventiveCareMainTable = function PreventiveCareMainTable() {
  /*eslint-disable */
  const [options, setOptions] = useState<IDropDownOption[]>([]);
  /*eslint-enable */
  const [refreshTable, setRefreshTable] = useState<boolean>(false);

  const [subjectId, setSubjectId] = useState<string>("");
  const [companyId, setCompanyId] = useState<string>("");
  const [resetRowSelection, setResetRowSelection] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState<boolean>(false);
  const [csvUploadInProgress, setCsvUploadInProgress] = useState(false);
  const [entries, setEntries] = useState({
    created: [],
    updated: [],
    skipped: [],
  });
  const [entriesModalOpen, setEntriesModalOpen] = useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [filterItemForApiCall, setFilterItemForApiCall] = useState<any>({
    filter: "",
    dateRange: [],
  });
  const [selectedStatus, setSelectedStatus] = useState<any[]>([]);
  const [departmentsForCompany, setDepartmentsForCompany] = useState<any[]>([]);
  const [rowCountForCurrentFilters, setRowCountForCurrentFilters] = useState<
    number | null
  >(null);

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  /*eslint-disable */
  const statusFilterOptions = getTranslatedStatusOptions();
  /*eslint-enable */

  const { t } = useTranslation();
  usePageHeading(t("preventive_care"));
  const appointmentRef = useRef(null);
  const { showConfirmationModalAsync, showConfirmationModal } =
    useConfirmationModal();

  const selectedExistingAppointmentRequestRef = useRef<string | null>(null);

  const handleNotification = (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean
  ) => {
    setNotificationMessage(message);
    setShowNotification(success);
    setNotificationVariant(variant);
  };

  const handleSuccessfulGet = (data: any) => {
    if (data && typeof data.count === "number") {
      setRowCountForCurrentFilters(data.count);
    }
  };

  const rowCountHelperElement = (count: number) => {
    if (rowCountForCurrentFilters === null) return null;

    return (
      <div className={styles.totalCountContainer}>
        <span>{t("preventivecare_count_for_current_filters")}:</span>
        <span className={styles.totalCountValue}>{count}</span>
      </div>
    );
  };

  const filteringOptions = [
    { value: "last_executed_on", label: t("last_executed_on") },
    { value: "next_appointment_on", label: t("next_appointment_on") },
    { value: "valid_until", label: t("valid_until") },
    { value: "last_offered_on", label: t("last_offered") },
  ];

  const legendItems = [
    {
      color: "#008c7d",
      inActiveColor: "#66bab1",
      label: t("valid"),
      value: "valid",
    },
    {
      color: "#ff6428",
      inActiveColor: "#ffa27e",
      label: t("planned"),
      value: "planned",
    },
    {
      color: "#dc3545",
      inActiveColor: "#ff4154",
      label: t("overdue"),
      value: "overdue",
    },
    {
      color: "#B3DDD8",
      inActiveColor: "#B3DDD8",
      label: t("last_offered"),
      value: "offered",
    },
    {
      color: "#9e9e9e",
      inActiveColor: "#bcbcbc",
      label: t("empty"),
      value: "empty",
    },
  ];

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubjectId(selectedOption.value);
      setCompanyId(selectedOption.companyId);
    }
  };

  const handleAppointmentRequestSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      selectedExistingAppointmentRequestRef.current = selectedOption.value;
    }
  };
  const handleResetAppointmentRequestInput = () => {
    selectedExistingAppointmentRequestRef.current = null;
  };
  const refreshParentTable = () => {
    setRefreshTable((prev) => !prev);
  };

  const resetAsyncInput = () => {
    setSubjectId("");
  };

  function getUniqueBuildings(data: any) {
    const uniqueBuildings = new Map();
    const result: any = [];

    if (Array.isArray(data.department_set)) {
      data.department_set.forEach((department: any) => {
        const building = department.building;

        if (building && !uniqueBuildings.has(building.id)) {
          uniqueBuildings.set(building.id, building);
          result.push(building);
        }
      });
    }

    return result;
  }

  const getExistingAppointmentRequestsForCompany = async (): Promise<
    { value: number; label: string }[]
  > => {
    const additionalUrlParameter: any = {};

    if (subdomain === "admin" && subjectId !== "") {
      additionalUrlParameter["company_subject_set__id__in"] = subjectId;
      additionalUrlParameter["include_draft"] = true;
    }

    return new Promise((resolve, reject) => {
      const apiRequestParams: any = {
        method: "get",
        entity: "appointmentRequest",
        successHandler: (res: any) => {
          try {
            const data = res.data.results;

            const tempAppointmentRequests = data.map((appR: any) => ({
              value: appR.id,
              label: `${appR.title}${
                appR.day_set.length
                  ? (() => {
                      const start = replaceDate(appR.day_set[0].start);
                      const end = replaceDate(
                        appR.day_set[appR.day_set.length - 1].end
                      );
                      return start === end
                        ? ` (${start})`
                        : ` (${start} - ${end})`;
                    })()
                  : ""
              }`,
            }));

            resolve(tempAppointmentRequests);
          } catch (err) {
            reject(err);
          }
        },
        failHandler: (error: any) => {
          reject(error);
        },
      };

      if (subdomain === "admin" && companyId !== "") {
        apiRequestParams.parametersToRender = {
          additionalUrlParameters: additionalUrlParameter,
        };
      }

      api.genericApiRequest(apiRequestParams);
    });
  };

  const getDepartmentsForCompany = () => {
    const additionalUrlParameter: any = {};

    if (subdomain === "admin" && subjectId !== "") {
      additionalUrlParameter["impersonate_subject"] = subjectId;
    }

    const apiRequestParams: any = {
      method: "get",
      entity: "department",
      successHandler: (res: any) => {
        const data = res.data.results;
        const tempDepartments = data.map((department: any) => ({
          value: department.value,
          label: department.name,
        }));
        setDepartmentsForCompany(tempDepartments);
      },
    };

    if (subdomain === "admin" && subjectId !== "") {
      apiRequestParams.parametersToRender = {
        additionalUrlParameters: additionalUrlParameter,
      };
    }

    api.genericApiRequest(apiRequestParams);
  };

  const downloadExampleCsv = async () => {
    try {
      const response = await fetch(
        "/api/v1/subjectExaminationDeadline/example_preventive_care_registry_csv/",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const result = await response.json();
        const errorContent = result
          ? result
          : t("error_downloading_example_csv");
        throw errorContent;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = t("example_preventive_care_registry_csv_filename");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error);
      setNotificationMessage(t("download_failed"));
      setNotificationVariant("error");
      setShowNotification(true);
    }
  };

  const handleCsvUpload = async (
    files: File[],
    { onUploadProgress }: FileUploadRequestOptions,
  ) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiRequest<Record<string, any>>({
        entity: "subjectExaminationDeadline",
        additionalRouteParts: { preventive_care_registry_csv: "/" },
        method: "post",
        submitData: formData,
        shouldNotImpersonate: true,
        disableGlobalSpinner: true,
        config: {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
        },
      });
      const result = response.data;

      setEntries({
        created: result["created entries"] || [],
        updated: result["updated entries"] || [],
        skipped: result["skipped entries"] || [],
      });

      setEntriesModalOpen(true);
      setNotificationMessage(t("upload_successful"));
      setNotificationVariant("success");
      setShowNotification(true);
      refreshParentTable();
    }
  };

  const entriesModalContent = (
    <div className={styles.entriesModalContainer}>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("created_entries")}:</p>
        <span>{entries?.created?.length || 0}</span>
      </section>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("updated_entries")}:</p>
        <span>{entries?.updated?.length || 0}</span>
      </section>
      <section className={styles.entriesSectionColumn}>
        <p className={styles.incorrectEntriesText}>{t("incorrect_entries")}:</p>
        <List>
          {entries?.skipped?.map((entry: any, index: number) => (
            <ListItem key={index} className={styles.errorListItem}>
              <ListItemText
                primary={entry?.email || t("unknown_entry")}
                secondary={
                  entry?.fields?.email?.join(", ") ||
                  entry?.fields?.valid_until?.join(", ") ||
                  entry?.reason ||
                  t("unknown_reason")
                }
                secondaryTypographyProps={{ color: "red" }}
              />
            </ListItem>
          ))}
        </List>
      </section>
    </div>
  );

  useEffect(() => {
    if ((subdomain === "admin" && subjectId !== "") || subdomain === "manage") {
      getDepartmentsForCompany();
    }
  }, [subjectId, subdomain]);

  useEffect(() => {
    setResetRowSelection((prev) => !prev);
  }, [subjectId, companyId]);

  const customDetailPanel = (data: any) => {
    const deadlineData = [
      ...(data?.original?.subjectexaminationdeadline_set || []),
    ];
    return (
      <GenericCrudTable
        key={String(refreshTable)}
        disableGlobalFilter={true}
        entity={"subjectExaminationDeadline"}
        entityAdapter={subjectExaminationDeadlineEntityAdapter}
        customTableData={deadlineData}
        enableColumnFilteringWithCustomTableData={true}
        enableSortingWithCustomTableData={true}
        customRowActionColSize={80}
        additionalRoutePartsForGet={{ preventive_care_registry: "/" }}
        searchId={subjectId}
        refreshTableAfterAction={refreshParentTable}
        enablePagination={false}
        additionalUrlParameter={{
          include_draft: "true",
          company_subject_id: subjectId,
        }}
        expandedDepth={true}
        disableAddItem={true}
        columns={[
          {
            header: t("id"),
            accessorKey: "id",
            id: "id",
            enableHiding: !subjectId ? false : true,
            visibleInShowHideMenu: !subjectId ? false : true,
          },
          {
            header: t("examination"),
            accessorKey: "examination.name",
            id: "examination",
            customColumnFilterId: "examination_name",
            entity: "examination",
            accessorFn: (row: any) => {
              if (row.examination) {
                return row?.examination?.name;
              }
            },
          },
          {
            header: t("last_offered_on"),
            id: "last_offered_on",
            accessorKey: "last_offered_on",
            sortingFn: "datetime",
            filterVariant: "date",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue())}</span>;
            },
          },

          {
            header: t("last_executed_on"),
            accessorKey: "last_executed_on",
            id: "last_executed_on",
            datePicker: true,
            sortingFn: "datetime",
            required: true,
            errorMessage: t("field_required"),
            filterVariant: "date",
            accessorFn: (row: any) => {
              const lastAppointmentId = row.last_appointment_id;
              const lastExecutedDate = replaceDate(row.last_executed_on);

              let output: any = lastExecutedDate;
              if (
                lastAppointmentId !== null &&
                lastAppointmentId !== undefined &&
                subdomain === "admin"
              ) {
                output = (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                    to={`/appointmentDashboard/${lastAppointmentId}`}
                  >
                    {lastExecutedDate}
                  </Link>
                );
              }
              return <span>{output}</span>;
            },
          },
          {
            header: t("next_appointment_on"),
            accessorKey: "next_appointment_on",
            id: "next_appointment_on",
            datePicker: true,
            filterVariant: "date",
            accessorFn: (row: any) => {
              const nextAppointmentId = row.next_appointment_id;
              if (!nextAppointmentId || !row.next_appointment_on) {
                return <span>-</span>;
              }
              const nextAppointmentOn = replaceDate(row.next_appointment_on);

              let output: any = nextAppointmentOn;
              if (subdomain === "admin") {
                output = (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                    to={`/appointmentDashboard/${nextAppointmentId}`}
                  >
                    {nextAppointmentOn}
                  </Link>
                );
              }
              return <span>{output}</span>;
            },
          },
          {
            header: t("valid_until"),
            accessorKey: "valid_until",
            id: "valid_until",
            enableForCreate: true,
            datePicker: true,
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue())}</span>;
            },
            sortingFn: "datetime",
            required: true,
            errorMessage: t("field_required"),
            filterVariant: "date",
          },

          {
            header: t("eligibility"),
            accessorKey: "eligibility",
            id: "eligibility",
            size: 255,
            textarea: true,
            enableForCreate: true,
          },
          {
            header: t("status"),
            accessorKey: "status",
            id: "status",
            Cell: ({ cell }: any) => {
              const value = cell.getValue();

              if (value === "valid") {
                return <span style={{ color: "#008c7d" }}>{t(value)}</span>;
              } else if (value === "planned") {
                return <span style={{ color: "#ff8353" }}>{t(value)}</span>;
              } else if (value === "offered") {
                return <span style={{ color: "#B3DDD8" }}>{t(value)}</span>;
              } else if (value === "empty") {
                return <span>{t(value)}</span>;
              }
              return <span style={{ color: "#dc3545" }}>{t("overdue")}</span>;
            },
            filterVariant: "select",
            filterSelectOptions: [
              { label: t("valid"), value: "valid" },
              { label: t("planned"), value: "planned" },
              { label: t("offered"), value: "offered" },
              { label: t("empty"), value: "empty" },
              { label: t("overdue"), value: "overdue" },
            ],
          },
        ]}
      />
    );
  };

  const chartLegendHeader = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        {legendItems.map((item, index) => (
          <CustomToggleButton
            key={index}
            item={item}
            onButtonClick={handleStatusFilter}
            isSelected={selectedStatus.includes(item.value)}
          />
        ))}
      </Box>
    );
  };

  const handleStatusFilter = (value: any) => {
    if (selectedStatus.includes(value)) {
      setSelectedStatus(selectedStatus.filter((status) => status !== value));
    } else {
      setSelectedStatus([...selectedStatus, value]);
    }
  };
  const handleDateChangeforApiCallItem = (dateRange: any) => {
    setFilterItemForApiCall((prevState: any) => ({
      ...prevState,
      dateRange: dateRange,
    }));
  };
  const handleDropDownChangeForApiCallItem = (filter: any) => {
    setFilterItemForApiCall((prevState: any) => ({
      ...prevState,
      filter: filter,
    }));
  };

  const handleClearFilter = () => {
    setFilterItemForApiCall({ filter: "", dateRange: [] });
  };

  const checkForShowFilter = () => {
    if (
      filterItemForApiCall.dateRange.length === 2 &&
      filterItemForApiCall.filter !== ""
    ) {
      return true;
    } else return false;
  };

  const hasAdditionalFiltersToReset =
    (filterItemForApiCall.filter !== "" &&
      filterItemForApiCall.dateRange.some(Boolean)) ||
    selectedStatus.length > 0;

  const handleResetAllFilters = () => {
    setFilterItemForApiCall({ filter: "", dateRange: [] });
    setSelectedStatus([]);
  };

  const exportMenuItems: ExportMenuItem[] = [
    {
      label: "export_as",
      format: "csv",
    },
  ];

  const customActionButton = () => {
    return (
      <div className={styles.customActionButtonWrapper}>
        <GenericButton onClick={() => setModalOpen(true)}>
          {t("apply_filter")}
        </GenericButton>

        {subdomain === "admin" && (
          <>
            <GenericButton onClick={() => setCsvUploadModalOpen(true)}>
              {t("csv_import")}
            </GenericButton>
            <GenericButton onClick={downloadExampleCsv}>
              {t("csv_import_sample_file")}
            </GenericButton>
          </>
        )}

        <ExportMenu
          entity="subjectExaminationDeadline"
          exportParameter="export_preventive_care_registry"
          menuItems={exportMenuItems}
          handleError={handleError}
          handleNotification={handleNotification}
          subjectId={subjectId}
        />

        {checkForShowFilter() && (
          <DateRangeFilterChip
            filterLabel={t(filterItemForApiCall.filter)}
            startDate={filterItemForApiCall.dateRange[0]}
            endDate={filterItemForApiCall.dateRange[1]}
            onDelete={handleClearFilter}
          />
        )}
      </div>
    );
  };

  const createNewAppointmentRequestForMultipleCustomers = async (data: any) => {
    const existingOptions = await getExistingAppointmentRequestsForCompany();
    const confirmationModalData = (
      <section>
        <p>
          {t("confirm_create_appointmentrequest_for_multiple_subjects", {
            number: data.person_subject_set.length,
          })}
        </p>
        <p>
          {t(
            "confirm_create_appointmentrequest_for_multiple_subjects_add_existing",
            {
              number: data.person_subject_set.length,
            }
          )}
        </p>
        <DropDown
          options={existingOptions}
          label={t("appointmentrequest_existing")}
          name={t("appointmentrequest_existing")}
          onChange={handleAppointmentRequestSelect}
          isMulti={false}
          isClearable={true}
          resetInput={handleResetAppointmentRequestInput}
        />
      </section>
    );
    const confirmCreate = await showConfirmationModalAsync(
      confirmationModalData
    );
    if (!confirmCreate) return;

    const dataToSubmit = {
      title: `${t("appointmentrequest_single")} - ${
        data.company.name
      } - ${replaceDate(dayjs())}`,
      company_subject_set: [data.company.subject_id],
      person_subject_set: data.person_subject_set,
      type: "standard_single",
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      let response: any;
      if (selectedExistingAppointmentRequestRef.current) {
        response = await new Promise((resolve, reject) => {
          api.genericApiRequest({
            method: "put",
            entity: "appointmentRequest",
            entityId: selectedExistingAppointmentRequestRef.current,
            submitData: {
              person_subject_set: data.person_subject_set,
              department_set: [],
              company_subject_set: [data.company.subject_id],
            },
            additionalRouteParts: { add_to_existing: "/" },
            successHandler: (res: any) => {
              resolve(res);
            },
            failHandler: (error: any) => {
              reject(error);
            },
            config: config,
          });
        });
      } else {
        response = await new Promise((resolve, reject) => {
          api.genericApiRequest({
            entity: "appointmentRequest",
            method: "post",
            successHandler: (res: any) => {
              resolve(res);
            },
            failHandler: (error: any) => {
              reject(error);
            },
            submitData: dataToSubmit,
            config: config,
          });
        });
      }
      const openInNewTab = (path: string) => {
        const base = window.location.origin;
        const fullUrl = `${base}${path}`;
        window.open(fullUrl, "_blank");
      };

      if (selectedExistingAppointmentRequestRef.current) {
        if (response.data.status === "draft") {
          openInNewTab(
            `/editappointmentrequest/${response?.data?.id}/settings`
          );
        } else
          openInNewTab(
            `/editappointmentrequest/${response?.data?.id}/building`
          );
        selectedExistingAppointmentRequestRef.current = null;
      } else {
        openInNewTab(`/editappointmentrequest/${response?.data?.id}/settings`);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const additionalTopToolBarButtonsForRowSelection = (seletedRows: any) => {
    const dataForAppointmentRequestCreation = {
      company: seletedRows[0].company || {},
      department_set: seletedRows
        .map((row: any) => row.department_set.id)
        .filter(Boolean),
      person_subject_set: seletedRows
        .map((row: any) => row.person.user.subject_id)
        .filter(Boolean),
    };
    return (
      <>
        <Tooltip
          title={t("create_new_appointmentrequest_for_selected_customers")}
          placement="top"
        >
          <GenericButton
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() =>
              createNewAppointmentRequestForMultipleCustomers(
                dataForAppointmentRequestCreation
              )
            }
          >
            {t("create_new_appointmentrequest")}
          </GenericButton>
        </Tooltip>
      </>
    );
  };

  const additionalUrlParameters: any = {};
  additionalUrlParameters["include_draft"] = true;
  additionalUrlParameters["company_subject_id"] = subjectId;

  if (
    filterItemForApiCall.dateRange.length === 2 &&
    filterItemForApiCall.filter !== ""
  ) {
    const tempStartDate = new Date(
      filterItemForApiCall.dateRange[0]
    ).toISOString();
    const tempEndDate = new Date(
      filterItemForApiCall.dateRange[1]
    ).toISOString();
    additionalUrlParameters[`${filterItemForApiCall.filter}_range`] = [
      dayjs(tempStartDate).format("YYYY-MM-DD"),
      dayjs(tempEndDate).format("YYYY-MM-DD"),
    ];
  }

  if (selectedStatus.length > 0) {
    additionalUrlParameters["status"] = selectedStatus.join(",");
  } else {
    delete additionalUrlParameters["status"];
  }

  const handleSuccessfullPost = (data: any) => {
    const modalData = (
      <ul>
        {Object.entries(data)
          .filter(([key]) => key !== "creation_results_by_examination")
          .map(([key, value]) => (
            <li key={key}>
              {t(key)}: {String(value)}
            </li>
          ))}
      </ul>
    );
    showConfirmationModal(modalData);
  };

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
  };
  return (
    <div>
      {subdomain === "admin" && (
        <DropDownSubjectSearch
          wrapperClassName={styles.dropDownSubjectSearch}
          options={options}
          onChange={handleSubjectSelect}
          subdomain={subdomain}
          isClearable={true}
          resetInput={resetAsyncInput}
          autofocus={true}
          customRef={appointmentRef}
          useGlobalState={true}
          queryParameter="companies"
        />
      )}
      {((subdomain === "admin" && subjectId) ||
        (subdomain !== "admin" && !subjectId)) && (
        <GenericCrudTable
          enableRowSelection={subdomain === "admin" ? true : false}
          resetRowSelection={resetRowSelection}
          // customTopToolBarAction={customActions}
          disableGlobalFilterMatchHighlighting={true}
          customTopToolBarActionButton={customActionButton()}
          // readOnlyMode={subdomain !== "admin"}
          refreshTableAfterAction={refreshParentTable}
          refreshTable={refreshTable}
          showRowActions={false}
          allowColumnFiltering={true}
          showColumnFilterbyDefault={true}
          withSubjectSearch={true}
          searchId={subjectId}
          
          entity={"subjectExaminationDeadline"}
          entityAdapter={subjectExaminationDeadlineEntityAdapter}
          additionalRoutePartsForGet={{ preventive_care_registry: "/" }}
          additionalUrlParameter={additionalUrlParameters}
          hasAdditionalFiltersToReset={hasAdditionalFiltersToReset}
          onResetAllFilters={handleResetAllFilters}
          expandedDepth={true}
          customDetailPanel={customDetailPanel}
          additionalCustomTopToolbarElementsForRowSelection={
            additionalTopToolBarButtonsForRowSelection
          }
          onSuccessfullPostCallback={handleSuccessfullPost}
          onSuccessfulGetHandler={handleSuccessfulGet}
          elementBelowGlobalSearchInput={rowCountHelperElement}
          additionalKeyForParameteresToRender={"examination_name__icontains"}
          columns={[
            {
              header: t("id"),
              accessorKey: "id",
              id: "id",
              enableHiding: !subjectId ? false : true,
              visibleInShowHideMenu: !subjectId ? false : true,
            },
            {
              header: t("subject"),
              accessorKey: "person.user.name",
              id: "subject_set",
              enableForCreate: true,
              asyncEntity: "subject",
              companyIdForFilteringAsync: companyId,
              asyncDropDown: true,
              multiselect: true,
              allowSubjectSearchForNonAdmin:
                subdomain === "manage" ? true : false,
              // required: true,
              // errorMessage: t("field_required"),
              customColumnFilterId: "subject_name",
              queryParameter: "users",
              customLabel: t("person"),
              accessorFn: (row: any) => {
                if (subdomain === "admin") {
                  return (
                    <NavigateToSubjectOverviewLink
                      subjectId={row.person.user.subject_id}
                      label={row.person.user.name}
                    />
                  );
                }
                return row.person.user.name;
              },
            },
            {
              header: t("departments"),
              accessorKey: "department_set",
              customColumnFilterId: "department_name",
              customFilterParameter: "__in",
              filterVariant: "multi-select",
              filterSelectOptions: departmentsForCompany,
              enableSorting: false,
              enableForCreate: true,
              entity: "department",
              multiselect: true,
              useGlobalStateToImpersonate: true,
              id: "department_set",
              accessorFn: (row: any) => {
                const departments: any = [];
                if (row.department_set) {
                  for (const key in row.department_set) {
                    if (row.department_set[key].name) {
                      departments.push(row.department_set[key].name);
                    }
                  }
                }
                return departments.join(", ");
              },
            },
            {
              header: t("building_location"),
              accessorKey: "building",
              id: "building",
              customColumnFilterId: "building_name",
              customFilterParameter: "__icontains",
              accessorFn: (row: any) => {
                const buildings = getUniqueBuildings(row);

                return (
                  <>
                    {buildings.map((building: any) => (
                      <div key={building.id}>
                        <span className={styles.buildingName}>
                          {building.name}
                        </span>
                        <br />
                        <span>
                          {building.street_house}, {building.zip}{" "}
                          {building.city}
                        </span>
                      </div>
                    ))}
                  </>
                );
              },
            },
            {
              id: "chart_legend",
              header: chartLegendHeader(),
              size: 400,
              enableSorting: false,
              enableColumnFilter: false,
              enableColumnActions: false,
              accessorFn: (row: any) => {
                const barChartValues = [];
                const validObjects = row?.subjectexaminationdeadline_set.filter(
                  (obj: any) => obj.status === "valid"
                ).length;
                const plannedObjects =
                  row?.subjectexaminationdeadline_set.filter(
                    (obj: any) => obj.status === "planned"
                  ).length;
                const overdueObjects =
                  row?.subjectexaminationdeadline_set.filter(
                    (obj: any) => obj.status === "overdue"
                  ).length;
                const offeredObject =
                  row?.subjectexaminationdeadline_set.filter(
                    (obj: any) => obj.status === "offered"
                  ).length;
                const noDataObjects =
                  row?.subjectexaminationdeadline_set.filter(
                    (obj: any) => obj.status === "empty"
                  ).length;
                barChartValues.push(
                  validObjects,
                  plannedObjects,
                  overdueObjects,
                  offeredObject,
                  noDataObjects
                );

                const totalExaminations = barChartValues.reduce(
                  (acc, value) => acc + value,
                  0
                );

                return (
                  <div className={styles.stacked_bar_chart}>
                    <div className={styles.stacked_bar}>
                      {barChartValues.map((value, index) => {
                        const widthPercentage =
                          totalExaminations === 0
                            ? 0
                            : (value / totalExaminations) * 100;

                        return (
                          <Tooltip
                            key={index}
                            title={`${legendItems[index].label}: ${value}`}
                            arrow
                            placement="top"
                          >
                            <div
                              key={index}
                              className={styles.bar_segment}
                              style={{
                                width: `${widthPercentage}%`,
                                backgroundColor: legendItems[index].color,
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                );
              },
            },
          ]}
          additionalColumnsForCreate={[
            {
              header: t("create_sed_for_company"),
              id: "create_sed_for_company",
              enableForCreate: true,
              checkBox: true,
              required: false,
              columnOnTop: true,
            },
            {
              header: t("examination"),
              accessorKey: "examination.name",
              id: "examination_set",
              enableForCreate: true,
              multiselect: true,
              entity: "examination",
              required: true,
              customColumnFilterId: "examination_name",
              errorMessage: t("field_required"),
              customLabelForGenericSelect:
                subdomain === "manage" && t("type_kind"),
            },
            {
              header: t("create_without_date"),
              id: "faux_overdue",
              enableForCreate: true,
              checkBox: true,
              required: false,
            },
            {
              header: t("executed_on"),
              accessorKey: "executed_on",
              id: "executed_on",
              enableForCreate: true,
              dateTimePicker: true,
              sortingFn: "datetime",
              required: true,
              errorMessage: t("field_required"),
              filterVariant: "date",
            },
            {
              header: t("valid_until"),
              accessorKey: "valid_until",
              id: "valid_until",
              enableForCreate: true,
              datePicker: true,
              sortingFn: "datetime",
              required: true,
              errorMessage: t("field_required"),
              filterVariant: "date",
            },
          ]}
        />
      )}
      {modalOpen && (
        <DateRangeFilterModal
          open={modalOpen}
          initialDateRange={filterItemForApiCall.dateRange}
          initialFilter={filterItemForApiCall.filter}
          defaultFilter="executed_on"
          filterOptions={filteringOptions}
          onClose={() => setModalOpen(false)}
          onApply={(dateRange, filter) => {
            handleDateChangeforApiCallItem(dateRange);
            if (filter) {
              handleDropDownChangeForApiCallItem(filter);
            }
            setModalOpen(false);
          }}
        />
      )}
      <Modal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        title={t("csv_import")}
        hideDialogActions={true}
        disableCancelButton={csvUploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={false}
          name="preventive_care_csv"
          folderId="preventiveCare"
          onCancel={() => setCsvUploadModalOpen(false)}
          customUploadHandler={handleCsvUpload}
          accept={[".csv"]}
          onUploadStateChange={setCsvUploadInProgress}
          handleUploadSuccess={(modalOpen: boolean) => {
            if (modalOpen) {
              setCsvUploadModalOpen(false);
            }
          }}
        />
      </Modal>
      <Modal
        open={entriesModalOpen}
        onSubmit={() => setEntriesModalOpen(false)}
        title={t("overview")}
        submitButtonText={t("confirm")}
        buttonPlacement="flex-end"
        hideCancelButton
      >
        {entriesModalContent}
      </Modal>

      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {errorModal}
    </div>
  );
};

const CustomToggleButton = ({ item, isSelected, onButtonClick }: any) => {
  return (
    <Box display="flex" alignItems="center" mr={2}>
      <Button
        onClick={() => onButtonClick(item.value)}
        name={item.value}
        sx={{
          width: 20,
          height: 20,
          minWidth: 0,
          borderRadius: "0.5rem",
          backgroundColor: isSelected ? item.color : "",
          border: !isSelected ? `2px solid ${item.color}` : "",
          marginRight: 1,
          "&:hover": {
            backgroundColor: isSelected ? item.color : item.inActiveColor,
            opacity: 0.8,
          },
        }}
      />
      <Typography variant="body2">{item.label}</Typography>
    </Box>
  );
};

export default React.memo(PreventiveCareMainTable);
